import React from 'react';
import styled from 'styled-components';
import { Body5 } from '@ui';
import { GhostButton } from '@ui/buttons';
import ErrorText from '@ui/ErrorText';

// The bulk-action bar: a count badge, the actions the caller renders, and a clear affordance.
// Visually the vault's SelectionActionBar, but IN FLOW above the table rather than fixed to the
// viewport: this one sits above one table on one card and scrolls with it. (It is not sticky —
// the window is the scroll container here, not the page, so a sticky bar would never stick.)
// Escape clears the selection unless the caller says a modal is open.

// `position: relative` only so the tint overlay below has a containing block.
const BarWrapper = styled.div`
  position: relative;
  margin-bottom: 12px;

  background: ${({ theme }) => theme.themeColors.componentNoOpacity};
  border-left: 3px solid ${({ theme }) => theme.themeColors.primaryEyeCatchingCtaButton};
  border-bottom: 2px solid ${({ theme }) => theme.themeColors.primaryEyeCatchingCtaButton};
  border-radius: 0 6px 6px 0;
  box-shadow: 0 2px 6px 1px rgba(0, 0, 0, 0.08);

  &:before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background: ${({ theme }) => theme.themeColors.primaryEyeCatchingCtaButton};
    opacity: 0.12;
    pointer-events: none;
  }
`;

const BarContent = styled.div`
  position: relative;
  min-height: 56px;
  padding: 8px 16px;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
`;

const Summary = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
`;

const CountBadge = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 24px;
  padding: 0 8px;
  background: ${({ theme }) => theme.themeColors.primaryCtaButton};
  border-radius: 12px;

  span,
  span * {
    color: ${({ theme }) => theme.themeColors.buttonText};
  }
`;

const Actions = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
`;

const Trailing = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

// A real button (GhostButton: focus ring, Enter / Space for free), drawn as the 28px circle the
// vault's bar uses, with UploadPanel's × glyph rather than CloseButton, which renders a <div>.
const ClearButton = styled(GhostButton)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border-radius: 50%;
  font-size: 18px;
  line-height: 1;
  color: ${({ theme }) => theme.themeColors.text};

  &:hover {
    background: ${({ theme }) => theme.themeColors.hoverComponentBackground};
  }
`;

export const SelectionBar = ({ count, noun = 'row', onClear, escapeClears = true, error, children }) => {
  React.useEffect(() => {
    if (!escapeClears || count === 0) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClear();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [escapeClears, count, onClear]);

  if (!count) return null;

  const nounLabel = count === 1 ? noun : `${noun}s`;
  const summaryLabel = `${count} ${nounLabel} selected`;

  return (
    <BarWrapper>
      <BarContent>
        <Summary role="status" aria-label={summaryLabel}>
          <CountBadge>
            <Body5 bold>{count}</Body5>
          </CountBadge>
          <Body5 bold>{`${nounLabel} selected`}</Body5>
          {error && <ErrorText>{error}</ErrorText>}
        </Summary>
        <Trailing>
          <Actions>{children}</Actions>
          <ClearButton aria-label="Clear selection" title="Clear selection" onClick={onClear}>
            <span aria-hidden="true">×</span>
          </ClearButton>
        </Trailing>
      </BarContent>
    </BarWrapper>
  );
};
